// ANGULAR
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpInterceptor } from '@angular/common/http';
// SERVICES
import { AuthService } from '@api-requests-services/auth.service';
// STORE
import { Store } from '@ngrx/store';
import { MainState } from '@store/main.reducer';
import { logout, refreshTokenError, refreshTokenSuccess } from '@store/session/session.actions';
// RXJS
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, filter, finalize, first, switchMap, takeUntil } from 'rxjs/operators';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private refreshTokenInProgress: boolean = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private store: Store<MainState>,
    private authService: AuthService,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return next.handle(request).pipe(
      catchError(
        error => {
          const url: string = request.url.toLowerCase();
          // No queremos refrescar token para algunas peticiones como login, logout, VerifyTotpMethods o refresh token en si mismas, por lo que devolvemos el error tal cual
          if (
            url.includes("login") || 
            url.includes("logout") || 
            url.includes("methods/totp/verify")
          ) { throw (error); }
          if (url.includes("refresh")) { this.store.dispatch(logout()); }// Si por lo que sea falla la llamada a refresh, hacemos logout directamente

          // Si el estado de error es diferente a 401 y 403 devolvemos el error tal cual, no lo interceptamos
          if (error.status !== 401 && error.status != 403) { throw (error); }

          const stopSignal$: Subject<boolean> = new Subject<boolean>();

          if (this.refreshTokenInProgress) {
            // Si refreshTokenInProgress es true, esperaremos hasta que refreshTokenSubject tenga un valor no nulo
            // – lo que significa que el nuevo token está listo y podemos reintentar la request de nuevo
            return this.refreshTokenSubject.pipe(
              filter(result => result !== null),
              first(),
              switchMap(token => next.handle(this.addTokenToRequest(request, token))),
              takeUntil(stopSignal$));
          } else {
            this.refreshTokenInProgress = true;

            // Ponemos refreshTokenSubject a null, para que las subsiguientes llamadas a la API esperarán hasta que el nuevo token sea recibido
            this.refreshTokenSubject.next(null);

            // Llamamos a refreshToken
            return this.authService.postRefreshToken().pipe(
              switchMap(
                response => {
                  // Cuando obtenemos la respuesta, establecemos refreshTokenInProgress a false
                  this.refreshTokenInProgress = false;
                  if (!!response.accessToken && !!response.refreshToken) {
                    this.store.dispatch(refreshTokenSuccess({ token: response.accessToken ?? '', refreshToken: response.refreshToken ?? '' }));
                  }
                  this.refreshTokenSubject.next(response);

                  return next.handle(this.addTokenToRequest(request, response.accessToken ?? ''));
                }
              ),
              catchError((error: any) => {
                this.refreshTokenInProgress = false;
                stopSignal$.next(true);
                this.store.dispatch(refreshTokenError({ error: error }));
                throw (error);
              }),
              finalize(() => {
                //En cualquier caso, tras la llamada a refreshToken ponemos a falso el refreshTokenInProgress
                stopSignal$.next(true);
                this.refreshTokenInProgress = false;
              }
              )
            )
          }
        }
      )
    );
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}